import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import {
  LayoutDashboard, Users2, UserCog, ScrollText, PlayCircle, Mail, FileBadge, BarChart3,
  BookOpen, Layers, FileDown, Loader2,
} from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
  { to: "/admin/executives", label: "Marketing Executives", icon: UserCog, testId: "nav-admin-executives" },
  { to: "/admin/students", label: "Students", icon: Users2, testId: "nav-admin-students" },
  { to: "/admin/inbox", label: "Inbox", icon: Mail, testId: "nav-admin-inbox" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testId: "nav-admin-audit" },
  { to: "/admin/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-admin-sample-report" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "nav-admin-analytics" },
  { to: "/admin/how-it-works", label: "How It Works", icon: BookOpen, testId: "nav-admin-how-it-works" },
  { to: "/admin/questions", label: "Question Bank", icon: Layers, testId: "nav-admin-questions" },
  { to: "/me/demo", label: "Demo Mode", icon: PlayCircle, testId: "nav-admin-demo" },
];

function downloadBlob(data, filename) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function aptitudeItemText(item) {
  if (item.type === "mcq") return `${item.prompt} [${item.options.map((o) => `${o.id}) ${o.label}`).join("  ")}]`;
  if (item.type === "same_different") return `Compare: '${item.pair[0]}' vs '${item.pair[1]}'`;
  if (item.type === "free_text") return item.prompt;
  if (item.type === "forced_choice") return `A) ${item.option_a.label}  |  B) ${item.option_b.label}`;
  return "";
}

export default function AdminQuestionBank() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    api.get("/admin/questions").then((res) => setData(res.data)).catch(() => setData(null));
  }, []);

  const wcppaFiltered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    return data.wcppa.filter((s) => !q || s.dimension.includes(q) || s.group.toLowerCase().includes(q) || s.alternates.A?.toLowerCase().includes(q));
  }, [data, search]);

  const aptitudeFiltered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    return data.aptitude.filter((it) => !q || it.subtest_name.toLowerCase().includes(q) || aptitudeItemText(it).toLowerCase().includes(q));
  }, [data, search]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/admin/questions/export.${format}`, { responseType: "blob" });
      downloadBlob(res.data, `careerstudy360_question_bank.${format}`);
    } catch {
      toast.error("Could not export the question bank");
    } finally {
      setExporting(null);
    }
  };

  return (
    <DashboardShell links={LINKS} activePath="/admin/questions">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="admin-question-bank-heading">Question Bank</h1>
          <p className="text-sm text-slate-500 mt-1">
            {data ? `${data.wcppa_total_slots} WCPPA slots (${data.wcppa_total_slots * 4} phrasings) + ${data.aptitude_total_items} Aptitude Test items` : "Loading..."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="export-questions-csv-button" disabled={exporting === "csv"} onClick={() => handleExport("csv")}>
            {exporting === "csv" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Export CSV
          </Button>
          <Button variant="outline" size="sm" data-testid="export-questions-pdf-button" disabled={exporting === "pdf"} onClick={() => handleExport("pdf")}>
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Export PDF
          </Button>
        </div>
      </div>

      <Input placeholder="Search by dimension, group or question text" value={search} data-testid="question-bank-search-input" onChange={(e) => setSearch(e.target.value)} className="max-w-md mb-6" />

      {!data && <p className="text-sm text-slate-400" data-testid="question-bank-loading">Loading question bank...</p>}

      {data && (
        <Tabs defaultValue="wcppa" data-testid="question-bank-tabs">
          <TabsList>
            <TabsTrigger value="wcppa" data-testid="tab-wcppa-questions">WCPPA Assessment ({wcppaFiltered.length})</TabsTrigger>
            <TabsTrigger value="aptitude" data-testid="tab-aptitude-questions">Aptitude Test ({aptitudeFiltered.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="wcppa">
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <Table data-testid="wcppa-questions-table">
                <TableHeader>
                  <TableRow><TableHead>Slot</TableHead><TableHead>Group</TableHead><TableHead>Dimension</TableHead><TableHead>Type</TableHead><TableHead>Question (Alternate A)</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {wcppaFiltered.slice(0, 300).map((s) => (
                    <TableRow key={s.slot_id} data-testid={`wcppa-question-row-${s.slot_id}`}>
                      <TableCell className="font-mono text-xs">{s.slot_id}</TableCell>
                      <TableCell className="text-xs">{s.group}</TableCell>
                      <TableCell className="text-xs">{s.dimension}</TableCell>
                      <TableCell className="text-xs">{s.type}</TableCell>
                      <TableCell className="text-sm max-w-md">{s.alternates.A}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="aptitude">
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <Table data-testid="aptitude-questions-table">
                <TableHeader>
                  <TableRow><TableHead>Item ID</TableHead><TableHead>Subtest</TableHead><TableHead>Type</TableHead><TableHead>Question</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {aptitudeFiltered.map((it) => (
                    <TableRow key={it.id} data-testid={`aptitude-question-row-${it.id}`}>
                      <TableCell className="font-mono text-xs">{it.id}</TableCell>
                      <TableCell className="text-xs">{it.subtest_name}</TableCell>
                      <TableCell className="text-xs">{it.type}</TableCell>
                      <TableCell className="text-sm max-w-md">{aptitudeItemText(it)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </DashboardShell>
  );
}
